import { useState, useCallback, useRef, useContext } from 'react';
import { useLazyQuery } from '@apollo/client';
import { delay } from '@devxdiscover/devhub-common';
import { UserContext } from '../../../contexts/UserContext';
import {
  ApplicationResourcesQuery,
  getQueryStringForAudit,
  MAX_APP_IDS_PER_BATCH,
} from '../CGAHierarchyAppsQuery';
import {
  CGAAppItem,
  AppResourceData,
  CloudAccountExportType,
  ReportMetadata,
  ExportStage,
  AlertState,
  ApplicationResourcesQueryResponse,
  ApplicationResourceEntry,
  HierarchyLevel,
  FailedFetchEntry,
} from '../types';
import { FILETYPE, HIERARCHY_LEVEL_LABELS } from '../utils/constants';
import {
  generateExcelReport,
  generateCSVReport,
  isRequestCancelled,
} from '../utils/exportHelpers';
import { useCnapBatchFetch } from './useCnapBatchFetch';
import { Device, CNAPCGAAudit } from '../../insights/types/InsightsTypes';

/** Retry configuration for failed fetches */
const RETRY_CONFIG = {
  maxRetries: 3,
  initialDelayMs: 1000,
  backoffMultiplier: 1.5,
  retryBatchSize: 10, // Smaller batches for retries
  maxDelayMs: 3000,
};

/**
 * Calculate backoff delay for retry attempts
 * @param attempt Current retry attempt number (0-indexed)
 */
function calculateBackoffDelay(attempt: number): number {
  const delayMs =
    RETRY_CONFIG.initialDelayMs *
    Math.pow(RETRY_CONFIG.backoffMultiplier, attempt);
  return Math.min(Math.ceil(delayMs), RETRY_CONFIG.maxDelayMs);
}

/**
 * State returned by the useResourceExport hook
 */
export interface UseResourceExportState {
  isExporting: boolean;
  exportProgress: number;
  exportFormat: FILETYPE;
  exportStage: ExportStage;
  exportAppCount: number;
  alert: AlertState;
  /** Apps that failed to fetch after all retry attempts */
  failedApps: FailedFetchEntry[];
  /** Number of apps being retried */
  retryingCount: number;
}

/**
 * Actions returned by the useResourceExport hook
 */
export interface UseResourceExportActions {
  setExportFormat: (format: FILETYPE) => void;
  handleExportFullReport: (
    applications: CGAAppItem[],
    selectedApps: CGAAppItem[],
    targetLogin: string,
    hierarchyLevel: HierarchyLevel,
  ) => Promise<void>;
  handleCancelExport: () => void;
  handleAlert: (alert: AlertState) => void;
  closeAlert: () => void;
}

/**
 * Custom hook for managing resource export state and functionality
 * @returns State and actions for resource export
 */
export const useResourceExport = (): [
  UseResourceExportState,
  UseResourceExportActions,
] => {
  const { login: currentUserLogin } = useContext(UserContext);
  const { fetchCnapDataBatch } = useCnapBatchFetch();

  const [loadApplicationResources, { fetchMore }] =
    useLazyQuery<ApplicationResourcesQueryResponse>(ApplicationResourcesQuery, {
      fetchPolicy: 'network-only',
    });

  // Export state
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [exportFormat, setExportFormat] = useState<FILETYPE>(FILETYPE.EXCEL);
  const [exportStage, setExportStage] = useState<ExportStage>('idle');
  const [exportAppCount, setExportAppCount] = useState(0);
  const [failedApps, setFailedApps] = useState<FailedFetchEntry[]>([]);
  const [retryingCount, setRetryingCount] = useState(0);
  const [alert, setAlert] = useState<AlertState>({
    open: false,
    message: '',
    severity: 'info',
  });

  const exportAbortControllerRef = useRef<AbortController | null>(null);
  const exportRunIdRef = useRef(0);
  const exportProgressRef = useRef(-1);
  const alertTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Handle alert state updates.
   * Alerts auto-dismiss after 8 seconds for info/success, 12 seconds for errors.
   */
  const handleAlert = useCallback((newAlert: AlertState) => {
    // Clear any existing auto-dismiss timer
    if (alertTimerRef.current) {
      clearTimeout(alertTimerRef.current);
      alertTimerRef.current = null;
    }

    setAlert(newAlert);

    // Set auto-dismiss timer if the alert is open
    if (newAlert.open) {
      const dismissMs =
        newAlert.severity === 'error' || newAlert.severity === 'warning'
          ? 12000
          : 8000;
      alertTimerRef.current = setTimeout(() => {
        setAlert(prev => ({ ...prev, open: false }));
        alertTimerRef.current = null;
      }, dismissMs);
    }
  }, []);

  /**
   * Close alert and clear any pending auto-dismiss timer
   */
  const closeAlert = useCallback(() => {
    if (alertTimerRef.current) {
      clearTimeout(alertTimerRef.current);
      alertTimerRef.current = null;
    }
    setAlert(prev => ({ ...prev, open: false }));
  }, []);

  /**
   * Cancel ongoing export
   */
  const handleCancelExport = useCallback(() => {
    exportRunIdRef.current += 1;
    exportAbortControllerRef.current?.abort();

    setIsExporting(false);
    setExportProgress(0);
    setExportStage('idle');
    setExportAppCount(0);
    setFailedApps([]);
    setRetryingCount(0);

    handleAlert({
      open: true,
      message: 'Export cancelled.',
      severity: 'info',
    });
  }, [handleAlert]);

  /**
   * Fetch resources for applications using applicationsPaged query + CNAP REST API.
   * Processes app IDs in batches with automatic retry for failed fetches.
   */
  const fetchResourcesInBatches = useCallback(
    async (
      apps: CGAAppItem[],
      abortSignal?: AbortSignal,
    ): Promise<{
      results: AppResourceData[];
      failedEntries: FailedFetchEntry[];
    }> => {
      const appIds = apps.map(app => app.applicationId);
      const totalApps = appIds.length;

      if (totalApps === 0) {
        return { results: [], failedEntries: [] };
      }

      exportProgressRef.current = -1;
      setFailedApps([]);
      setRetryingCount(0);

      // Create a map of app details for quick lookup
      const appDetailsMap = new Map<number, CGAAppItem>();
      apps.forEach(app => appDetailsMap.set(app.applicationId, app));

      // Track failed app IDs for retry
      const failedGraphqlAppIds = new Set<number>();
      const failedGraphqlErrors = new Map<number, string>();

      // Calculate total batches for progress (including potential retry phase)
      const graphqlBatches = Math.ceil(totalApps / MAX_APP_IDS_PER_BATCH);
      let graphqlCompletedBatches = 0;

      // Helper to update progress (10-99% range, 100% only at file generation)
      const updateProgress = (
        graphqlProgress: number,
        cnapDone: boolean,
        retryProgress = 0,
      ) => {
        // Progress distribution with retry phase:
        // - GraphQL: 10-65% (55% range)
        // - Retry: 65-75% (10% range)
        // - CNAP: 75-99% (24% range)
        const graphqlPortion = 10 + graphqlProgress * 0.55;
        const retryPortion = retryProgress * 0.1;
        const cnapPortion = cnapDone ? 24 : 0;
        const rawProgress = graphqlPortion + retryPortion + cnapPortion;
        // Cap at 99% - we'll set to 100% right before file generation
        const displayProgress = Math.min(99, Math.round(rawProgress));
        if (displayProgress !== exportProgressRef.current) {
          exportProgressRef.current = displayProgress;
          setExportProgress(displayProgress);
        }
      };

      // Start CNAP fetch immediately (runs in parallel with GraphQL)
      const cnapResult = fetchCnapDataBatch(appIds, abortSignal);
      let cnapDataMap: Map<number, CNAPCGAAudit[]> = new Map();
      let cnapDone = false;

      cnapResult
        .then(() => {
          cnapDone = true;
          const graphqlProgress =
            (graphqlCompletedBatches / graphqlBatches) * 100;
          updateProgress(graphqlProgress, true);
        })
        .catch(() => {
          cnapDone = true;
        });

      // Process GraphQL requests in batches
      setExportStage('graphql');
      const results: AppResourceData[] = [];
      const resultsByAppId = new Map<number, AppResourceData>();
      const appsNeedingDevicePagination: Array<{
        appId: number;
        totalCount: number;
      }> = [];

      const toExportDevice = (
        device: unknown,
        applicationId: number,
        applicationName: string,
        ownerName: string,
      ): Device => {
        const d = device as Device;
        return {
          ...d,
          type: d.type || '',
          href: d.href || '',
          serialNumber: d.serialNumber || '',
          make: d.make || '',
          model: d.model || '',
          applicationsPaged: {
            applications: [
              {
                name: applicationName,
                applicationId,
                owner: {
                  displayName: ownerName,
                },
              },
            ],
          },
          osInfo: {
            make: d.osInfo?.make || '',
            name: d.osInfo?.name || '',
            edition: d.osInfo?.edition || '',
            release: d.osInfo?.release || '',
            version: d.osInfo?.version || '',
          },
        };
      };

      /**
       * Process a single application entry from GraphQL response
       * @param entry Application entry from GraphQL response
       */
      const processApplicationEntry = (
        entry: ApplicationResourceEntry,
      ): AppResourceData => {
        const appDetails = appDetailsMap.get(entry.applicationId);
        const resolvedAppName =
          entry.name || appDetails?.name || `App ${entry.applicationId}`;
        const resolvedOwnerName =
          entry.owner?.displayName || appDetails?.owner?.displayName || '';

        // Transform devices to expected format
        const devices = (entry.devicesPaged?.devices || []).map(device =>
          toExportDevice(
            device,
            entry.applicationId,
            resolvedAppName,
            resolvedOwnerName,
          ),
        );

        const deviceTotalCount =
          typeof entry.devicesPaged?.totalCount === 'number'
            ? entry.devicesPaged.totalCount
            : devices.length;

        if (devices.length < deviceTotalCount) {
          appsNeedingDevicePagination.push({
            appId: entry.applicationId,
            totalCount: deviceTotalCount,
          });
        }

        // Transform cloud accounts to export format
        const cloudAccounts: CloudAccountExportType[] = (
          entry.cloudAccounts || []
        ).map(ca => ({
          'Application ID': entry.applicationId,
          'Application Name': resolvedAppName,
          'Account name': ca.name || '',
          'Account ID': ca.id,
          Platform: ca.platform || '',
          Environment: ca.environment || '',
          'Technical owner': resolvedOwnerName,
          'Associated applications': resolvedAppName,
        }));

        return {
          appId: entry.applicationId,
          appName: resolvedAppName,
          devices,
          cloudAccounts,
          cnapData: [],
          owner: resolvedOwnerName,
          status: entry.status || appDetails?.status || '',
          appGroup: appDetails?.appGroup,
          serviceDomain: appDetails?.serviceDomain,
        };
      };

      /**
       * Fetch a batch of applications with retry support.
       * Returns two distinct failure buckets:
       * - noResourceAppIds: query succeeded but app was absent (has no resources, never retry)
       * - errorAppIds: query threw an exception (network/server error, worth retrying)
       * @param batchAppIds Array of application IDs to fetch
       * @param maxAttempts Maximum number of attempts (default: maxRetries+1 for PHASE 1, 1 for PHASE 2)
       * @param useInitialLoad Whether to use loadApplicationResources for the first attempt (only true for the very first batch of PHASE 1)
       */
      const fetchBatchWithRetry = async (
        batchAppIds: number[],
        maxAttempts = RETRY_CONFIG.maxRetries + 1,
        useInitialLoad = false,
      ): Promise<{
        successful: ApplicationResourceEntry[];
        noResourceAppIds: number[];
        errorAppIds: number[];
        errorMessage?: string;
      }> => {
        const abortContext = abortSignal
          ? { fetchOptions: { signal: abortSignal } }
          : undefined;

        let lastError: string | undefined;

        for (let attempt = 0; attempt < maxAttempts; attempt++) {
          if (abortSignal?.aborted) {
            throw new DOMException('Aborted', 'AbortError');
          }

          try {
            const response =
              useInitialLoad && attempt === 0
                ? await loadApplicationResources({
                    variables: {
                      filter: { applicationIds: batchAppIds },
                      devicesPagination: { limit: 100, offset: 0 },
                    },
                    context: abortContext,
                  })
                : await (fetchMore
                    ? fetchMore({
                        variables: {
                          filter: { applicationIds: batchAppIds },
                          devicesPagination: { limit: 100, offset: 0 },
                        },
                        context: abortContext,
                        updateQuery: (previous, { fetchMoreResult }) =>
                          fetchMoreResult ?? previous,
                      })
                    : loadApplicationResources({
                        variables: {
                          filter: { applicationIds: batchAppIds },
                          devicesPagination: { limit: 100, offset: 0 },
                        },
                        context: abortContext,
                      }));

            const batchApplications =
              response.data?.resources?.applicationsPaged?.applications ?? [];

            // Apps absent from a successful response simply have no resources
            const returnedAppIds = new Set(
              batchApplications.map(app => app.applicationId),
            );
            const noResourceAppIds = batchAppIds.filter(
              id => !returnedAppIds.has(id),
            );

            return {
              successful: batchApplications,
              noResourceAppIds,
              errorAppIds: [],
            };
          } catch (err) {
            if (isRequestCancelled(err)) {
              throw err;
            }

            lastError = err instanceof Error ? err.message : 'Unknown error';

            // Don't retry on last attempt
            if (attempt < maxAttempts - 1) {
              const delayMs = calculateBackoffDelay(attempt);
              // eslint-disable-next-line no-console
              console.info(
                `GraphQL batch fetch attempt ${attempt + 1} failed, retrying in ${delayMs}ms...`,
              );
              await delay(delayMs);
            }
          }
        }

        // All retries exhausted — genuine error, not a no-resource case
        return {
          successful: [],
          noResourceAppIds: [],
          errorAppIds: batchAppIds,
          errorMessage: lastError,
        };
      };

      /**
       * Fetch a single device page for one application at a specific offset.
       * No internal retry — strict failure: a fetch error flags the app as having incomplete data.
       * @param appId Application ID to fetch devices for
       * @param offset Pagination offset (must be > 0; offset=0 was already fetched in PHASE 1)
       * @param appName Resolved application name used for device transformation
       * @param ownerName Resolved owner display name used for device transformation
       * @param abortContext Apollo context carrying the abort signal
       */
      const fetchSingleDevicePage = async (
        appId: number,
        offset: number,
        appName: string,
        ownerName: string,
        abortContext: Record<string, unknown> | undefined,
      ): Promise<Device[]> => {
        if (abortSignal?.aborted) {
          throw new DOMException('Aborted', 'AbortError');
        }

        const response = fetchMore
          ? await fetchMore({
              variables: {
                filter: { applicationIds: [appId] },
                devicesPagination: { limit: 100, offset },
              },
              context: abortContext,
              updateQuery: (previous, { fetchMoreResult }) =>
                fetchMoreResult ?? previous,
            })
          : await loadApplicationResources({
              variables: {
                filter: { applicationIds: [appId] },
                devicesPagination: { limit: 100, offset },
              },
              context: abortContext,
            });

        const entry =
          response.data?.resources?.applicationsPaged?.applications?.[0];
        return (entry?.devicesPaged?.devices || []).map(device =>
          toExportDevice(device, appId, appName, ownerName),
        );
      };

      // === PHASE 1: Initial batch processing (up to 3 batches in parallel per chunk) ===
      const BATCH_CONCURRENCY = 3;
      // Up to 20 individual device pages across all apps fire simultaneously in PHASE 3
      const DEVICE_PAGE_CONCURRENCY = 20;
      const allBatches: number[][] = [];
      for (let i = 0; i < totalApps; i += MAX_APP_IDS_PER_BATCH) {
        allBatches.push(appIds.slice(i, i + MAX_APP_IDS_PER_BATCH));
      }

      for (let i = 0; i < allBatches.length; i += BATCH_CONCURRENCY) {
        if (abortSignal?.aborted) {
          throw new DOMException('Aborted', 'AbortError');
        }

        const chunk = allBatches.slice(i, i + BATCH_CONCURRENCY);
        const settled = await Promise.allSettled(
          chunk.map((batchAppIds, idx) =>
            // Only the very first batch of the entire export uses loadApplicationResources to initialise the query
            fetchBatchWithRetry(
              batchAppIds,
              RETRY_CONFIG.maxRetries + 1,
              i === 0 && idx === 0,
            ),
          ),
        );

        settled.forEach((settledResult, idx) => {
          if (settledResult.status === 'rejected') {
            if (isRequestCancelled(settledResult.reason)) {
              throw settledResult.reason;
            }
            // Treat as exception-failed — add all batch apps to the retry queue
            chunk[idx].forEach(appId => failedGraphqlAppIds.add(appId));
            return;
          }

          const batchResult = settledResult.value;

          // Process successful entries
          batchResult.successful.forEach(entry => {
            const row = processApplicationEntry(entry);
            results.push(row);
            resultsByAppId.set(row.appId, row);
          });

          // Apps absent from a successful response have no resources — include as empty rows, never retry
          batchResult.noResourceAppIds.forEach(appId => {
            if (!resultsByAppId.has(appId)) {
              const appDetails = appDetailsMap.get(appId);
              const row: AppResourceData = {
                appId,
                appName: appDetails?.name || `App ${appId}`,
                devices: [],
                cloudAccounts: [],
                cnapData: [],
                owner: appDetails?.owner?.displayName || '',
                status: appDetails?.status || '',
                appGroup: appDetails?.appGroup,
                serviceDomain: appDetails?.serviceDomain,
              };
              results.push(row);
              resultsByAppId.set(appId, row);
            }
          });

          // Track exception-failed apps for retry
          batchResult.errorAppIds.forEach(appId => {
            failedGraphqlAppIds.add(appId);
            if (batchResult.errorMessage) {
              failedGraphqlErrors.set(appId, batchResult.errorMessage);
            }
          });
        });

        graphqlCompletedBatches += chunk.length;
        const graphqlProgress =
          (graphqlCompletedBatches / graphqlBatches) * 100;
        updateProgress(graphqlProgress, cnapDone);
      }

      // === PHASE 2: Retry failed GraphQL fetches in smaller batches ===
      if (failedGraphqlAppIds.size > 0 && !abortSignal?.aborted) {
        setExportStage('retrying');
        const failedIds = Array.from(failedGraphqlAppIds);
        setRetryingCount(failedIds.length);

        // eslint-disable-next-line no-console
        console.info(
          `Retrying ${failedIds.length} failed application(s) in smaller batches...`,
        );

        let retriedCount = 0;
        for (
          let i = 0;
          i < failedIds.length;
          i += RETRY_CONFIG.retryBatchSize
        ) {
          if (abortSignal?.aborted) {
            break;
          }

          const retryBatch = failedIds.slice(
            i,
            i + RETRY_CONFIG.retryBatchSize,
          );
          // Single attempt — PHASE 1 already exhausted its retries for exception failures
          const retryResult = await fetchBatchWithRetry(retryBatch, 1);

          // Process any successful retries
          retryResult.successful.forEach(entry => {
            const row = processApplicationEntry(entry);
            results.push(row);
            resultsByAppId.set(row.appId, row);
            failedGraphqlAppIds.delete(entry.applicationId);
          });

          // Apps absent even after retry genuinely have no resources — include as empty rows
          retryResult.noResourceAppIds.forEach(appId => {
            failedGraphqlAppIds.delete(appId);
            if (!resultsByAppId.has(appId)) {
              const appDetails = appDetailsMap.get(appId);
              const row: AppResourceData = {
                appId,
                appName: appDetails?.name || `App ${appId}`,
                devices: [],
                cloudAccounts: [],
                cnapData: [],
                owner: appDetails?.owner?.displayName || '',
                status: appDetails?.status || '',
                appGroup: appDetails?.appGroup,
                serviceDomain: appDetails?.serviceDomain,
              };
              results.push(row);
              resultsByAppId.set(appId, row);
            }
          });

          // Update error messages for still-exception-failed apps
          retryResult.errorAppIds.forEach(appId => {
            if (retryResult.errorMessage) {
              failedGraphqlErrors.set(appId, retryResult.errorMessage);
            }
          });

          retriedCount += retryBatch.length;
          const retryProgress = (retriedCount / failedIds.length) * 100;
          updateProgress(100, cnapDone, retryProgress);
        }
      }

      // === PHASE 3: Fetch remaining device pages (global flat pool) ===
      // devicePageFailures is declared outside the if-block so the final entries builder
      // can reference it below.
      const devicePageFailures = new Map<number, string>();

      if (appsNeedingDevicePagination.length && !abortSignal?.aborted) {
        const abortContext = abortSignal
          ? { fetchOptions: { signal: abortSignal } }
          : undefined;

        // Build a flat list of every (appId, offset) pair across all apps, skipping
        // offset=0 because PHASE 1 already fetched and stored those devices.
        type DevicePageRequest = {
          appId: number;
          offset: number;
          appName: string;
          ownerName: string;
        };
        const pageRequests: DevicePageRequest[] = [];
        const seenDeviceAppIds = new Set<number>();

        appsNeedingDevicePagination.forEach(({ appId, totalCount }) => {
          if (seenDeviceAppIds.has(appId)) return;
          seenDeviceAppIds.add(appId);
          const resultRow = resultsByAppId.get(appId);
          const appName = resultRow?.appName || `App ${appId}`;
          const ownerName = resultRow?.owner || '';
          for (let offset = 100; offset < totalCount; offset += 100) {
            pageRequests.push({ appId, offset, appName, ownerName });
          }
        });

        // Accumulate fetched pages: appId → offset → devices
        const devicePageResults = new Map<number, Map<number, Device[]>>();

        for (let i = 0; i < pageRequests.length; i += DEVICE_PAGE_CONCURRENCY) {
          if (abortSignal?.aborted) {
            break;
          }

          const chunk = pageRequests.slice(i, i + DEVICE_PAGE_CONCURRENCY);
          const settled = await Promise.allSettled(
            chunk.map(req =>
              fetchSingleDevicePage(
                req.appId,
                req.offset,
                req.appName,
                req.ownerName,
                abortContext,
              ),
            ),
          );

          settled.forEach((result, idx) => {
            const { appId, offset } = chunk[idx];
            if (result.status === 'fulfilled') {
              let appPages = devicePageResults.get(appId);
              if (!appPages) {
                appPages = new Map<number, Device[]>();
                devicePageResults.set(appId, appPages);
              }
              appPages.set(offset, result.value);
            } else if (isRequestCancelled(result.reason)) {
              throw result.reason;
            } else if (!devicePageFailures.has(appId)) {
              const errMsg =
                result.reason instanceof Error
                  ? result.reason.message
                  : 'Unknown error';
              devicePageFailures.set(appId, errMsg);
            }
          });
        }

        // Merge fetched pages back into results (sorted by offset ascending)
        seenDeviceAppIds.forEach(appId => {
          const row = resultsByAppId.get(appId);
          if (!row) return;
          const extraPages = devicePageResults.get(appId);
          if (extraPages) {
            const sortedOffsets = Array.from(extraPages.keys()).sort(
              (a, b) => a - b,
            );
            sortedOffsets.forEach(offset => {
              row.devices.push(...(extraPages.get(offset) || []));
            });
          }
        });
      }

      // === PHASE 4: Wait for CNAP to complete and handle retries ===
      if (!abortSignal?.aborted) {
        if (!cnapDone) {
          setExportStage('cnap');
        }

        try {
          const cnapFinalResult = await cnapResult;
          cnapDataMap = cnapFinalResult.data;
        } catch {
          // CNAP failed, continue with empty data
        }

        setExportStage('finalizing');

        // Merge CNAP data into results
        results.forEach(result => {
          result.cnapData = cnapDataMap.get(result.appId) || [];
        });

        setExportProgress(100);
      }

      // === Build final failed entries list ===
      const finalFailedEntries: FailedFetchEntry[] = [];
      failedGraphqlAppIds.forEach(appId => {
        const appDetails = appDetailsMap.get(appId);
        finalFailedEntries.push({
          appId,
          appName: appDetails?.name || `App ${appId}`,
          errorMessage:
            failedGraphqlErrors.get(appId) ||
            'Failed to fetch application resources',
          attemptCount: RETRY_CONFIG.maxRetries + 1,
        });

        // Add fallback entry for failed apps
        if (!resultsByAppId.has(appId)) {
          const row: AppResourceData = {
            appId,
            appName: appDetails?.name || `App ${appId}`,
            devices: [],
            cloudAccounts: [],
            cnapData: [],
            owner: appDetails?.owner?.displayName || '',
            status: appDetails?.status || '',
            appGroup: appDetails?.appGroup,
            serviceDomain: appDetails?.serviceDomain,
          };
          results.push(row);
          resultsByAppId.set(appId, row);
        }
      });

      // Add device pagination failures (apps with incomplete device data from PHASE 3)
      devicePageFailures.forEach((errMsg, appId) => {
        const row = resultsByAppId.get(appId);
        finalFailedEntries.push({
          appId,
          appName: row?.appName || `App ${appId}`,
          errorMessage: `Incomplete device data: ${errMsg}`,
          attemptCount: 1,
        });
      });

      setExportStage('idle');
      setRetryingCount(0);
      return { results, failedEntries: finalFailedEntries };
    },
    [fetchCnapDataBatch, fetchMore, loadApplicationResources],
  );

  /**
   * Handle full compliance report export
   */
  const handleExportFullReport = useCallback(
    async (
      applications: CGAAppItem[],
      selectedApps: CGAAppItem[],
      targetLogin: string,
      hierarchyLevel: HierarchyLevel,
    ) => {
      const appsToExport =
        selectedApps.length > 0 ? selectedApps : applications;

      if (appsToExport.length === 0) {
        handleAlert({
          open: true,
          message: 'No applications to export.',
          severity: 'error',
        });
        return;
      }

      exportAbortControllerRef.current?.abort();
      const abortController = new AbortController();
      exportAbortControllerRef.current = abortController;

      exportRunIdRef.current += 1;
      const runId = exportRunIdRef.current;

      setIsExporting(true);
      setExportProgress(0);
      setExportStage('graphql');
      setExportAppCount(appsToExport.length);
      setFailedApps([]);

      // Smooth startup animation: 0% -> 10% over 300ms
      const startupSteps = 10;
      const startupDelay = 30; // 300ms total
      for (let i = 1; i <= startupSteps; i++) {
        await new Promise(resolve => setTimeout(resolve, startupDelay));
        if (runId !== exportRunIdRef.current) return;
        setExportProgress(i);
      }

      try {
        const { results: allAppsData, failedEntries } =
          await fetchResourcesInBatches(appsToExport, abortController.signal);

        if (runId !== exportRunIdRef.current) {
          return;
        }

        // Update failed apps state for UI visibility
        setFailedApps(failedEntries);

        // Progress is at 99% now, move to 100% right before file generation
        setExportProgress(100);

        // Generate report metadata
        const reportMetadata: ReportMetadata = {
          generatedAt: new Date().toISOString(),
          generatedBy: currentUserLogin || 'Unknown',
          hierarchyLevel:
            hierarchyLevel === HierarchyLevel.DIRECT_REPORTS
              ? HIERARCHY_LEVEL_LABELS.DIRECT_REPORTS
              : HIERARCHY_LEVEL_LABELS.FULL_REPORTING_HIERARCHY,
          targetUser: targetLogin || 'Unknown',
          totalApplications: appsToExport.length,
          fusionQuery: getQueryStringForAudit(
            targetLogin || '',
            hierarchyLevel,
          ),
          applicationIds: appsToExport.map(app => app.applicationId),
        };

        // Export based on format
        if (exportFormat === FILETYPE.EXCEL) {
          generateExcelReport(
            allAppsData,
            reportMetadata,
            targetLogin,
            currentUserLogin || '',
          );
        } else {
          generateCSVReport(allAppsData, reportMetadata, targetLogin);
        }

        // Show appropriate success message
        if (failedEntries.length > 0) {
          handleAlert({
            open: true,
            message: `CGA Hierarchy Report exported with ${failedEntries.length} application(s) having incomplete data. Check the report for details.`,
            severity: 'warning',
          });
        } else {
          handleAlert({
            open: true,
            message: 'CGA Hierarchy Report exported successfully.',
            severity: 'success',
          });
        }
      } catch (err: unknown) {
        if (isRequestCancelled(err) || abortController.signal.aborted) {
          handleAlert({
            open: true,
            message: 'Export cancelled.',
            severity: 'info',
          });
          return;
        }

        handleAlert({
          open: true,
          message: 'Error exporting report. Please try again.',
          severity: 'error',
        });
      } finally {
        if (runId === exportRunIdRef.current) {
          setIsExporting(false);
          setExportProgress(0);
          setExportStage('idle');
          setExportAppCount(0);
          setRetryingCount(0);
        }
      }
    },
    [currentUserLogin, exportFormat, fetchResourcesInBatches, handleAlert],
  );

  /**
   * Handle setting export format
   */
  const handleSetExportFormat = useCallback((format: FILETYPE) => {
    setExportFormat(format);
  }, []);

  const state: UseResourceExportState = {
    isExporting,
    exportProgress,
    exportFormat,
    exportStage,
    exportAppCount,
    alert,
    failedApps,
    retryingCount,
  };

  const actions: UseResourceExportActions = {
    setExportFormat: handleSetExportFormat,
    handleExportFullReport,
    handleCancelExport,
    handleAlert,
    closeAlert,
  };

  return [state, actions];
};
